/**
 * CDN Provider Configuration
 *
 * Controls which CDN provider is used for image/video transformation and cache purging.
 *
 * NEXT_PUBLIC_CDN_PROVIDER       — global toggle: 'akamai' | 'cloudflare' (default: 'cloudflare')
 */

export type CdnProvider = 'akamai' | 'cloudflare';

export const CDN_PROVIDER: CdnProvider =
  (process.env.NEXT_PUBLIC_CDN_PROVIDER as CdnProvider) || 'cloudflare';

export const isCloudflare = CDN_PROVIDER === 'cloudflare';
export const isAkamai = CDN_PROVIDER === 'akamai';

const parseBooleanEnv = (value: string | undefined): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

/**
 * CDN Cache Purge Feature Flags
 *
 * Independent feature flags that control which CDN providers participate in cache purging.
 * Both can be enabled simultaneously to purge caches on multiple CDNs in parallel.
 *
 * CLOUDFLARE_PURGE_ENABLED: Enable Cloudflare cache purge by tags (supports multiple zones)
 * AKAMAI_PURGE_ENABLED: Enable Akamai cache purge by tags (delete/invalidate)
 *
 * These flags are independent of CDN_PROVIDER which controls image/video proxying.
 */
const cloudflarePurgeFlag = parseBooleanEnv(
  process.env.CLOUDFLARE_PURGE_ENABLED,
);
const akamaiPurgeFlag = parseBooleanEnv(process.env.AKAMAI_PURGE_ENABLED);

/**
 * Backward-compatible defaults:
 * - If purge flags are not set, follow CDN_PROVIDER behavior.
 * - If flags are explicitly set, they take precedence.
 */
export const CLOUDFLARE_PURGE_ENABLED =
  cloudflarePurgeFlag ?? CDN_PROVIDER === 'cloudflare';

export const AKAMAI_PURGE_ENABLED =
  akamaiPurgeFlag ?? CDN_PROVIDER === 'akamai';

/**
 * Returns the base URL for CDN image/video proxying.
 *
 * Cloudflare: Uses NEXT_PUBLIC_CF_CDN_URL (e.g., https://br-cdn-poc.us)
 * Akamai: Uses NEXT_PUBLIC_IMAGE_PROXY (e.g., https://www.breitling.com/)
 *
 * @param provider - Resolved CDN provider. Defaults to the global CDN_PROVIDER.
 */
export const getCdnBaseUrl = (provider?: CdnProvider): string => {
  const resolved = provider ?? CDN_PROVIDER;
  if (resolved === 'cloudflare') {
    return process.env.NEXT_PUBLIC_CF_CDN_URL || 'https://br-cdn-poc.us';
  }
  return (
    process.env.NEXT_PUBLIC_IMAGE_PROXY || process.env.NEXT_PUBLIC_URL || ''
  );
};
